<!--
  Worklist_FilterBar - Shared entity filter bar for all worklist pages.

  Filters accumulate across entity types. Select a type, pick entities,
  switch to another type, pick more — all selections are preserved.

  Props:
    sensitiveLabels:     Defaults to true: the picker options, the selected
                         label and the active-filter chips are entity names
                         (practices, providers, facilities), so Demo Mode masks
                         them and empties the picker's search box. Pass false
                         only for a caller whose entity types are never
                         identifying.
    storageKey:          Optional localStorage key for filter persistence.
    initialFilters:      Optional object { filters, type } for parent-managed
                         persistence. Used instead of storageKey when the parent
                         controls save/restore (e.g., via BrowserStore).
    excludeTypes:        Entity type names to hide from the type dropdown.
    demographicFilters:  Optional array of demographic filter keys to enable.
                         Valid keys: 'race_ethnicity', 'language'
                         Example: ['race_ethnicity', 'language']

  Emits:
    @filter-changed({ entity_ids, demographic_filters })
-->
<template>
  <div class="bg-white border-b border-gray-200 px-4 py-2">
    <div class="flex items-end flex-wrap gap-3">
      <template v-if="loading">
        <!-- Skeleton: Filter By dropdown -->
        <div class="max-w-48">
          <div class="h-3 w-12 bg-gray-200 rounded mb-1.5 animate-pulse" />
          <div class="h-7 w-36 bg-gray-100 rounded border border-gray-200 animate-pulse" />
        </div>
        <!-- Skeleton: My Practices pill -->
        <div class="h-7 w-24 bg-gray-100 rounded-full border border-gray-200 animate-pulse" />
      </template>
      <template v-else>
        <!-- Entity Type Dropdown -->
        <div class="max-w-48">
          <label class="block text-xs font-medium text-gray-600 mb-1">Filter By</label>
          <select
            v-model="selectedType"
            @change="onTypeChange"
            class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-blue-dark focus:ring-offset-4 focus:border-blue-dark"
          >
            <option :value="null">Select type...</option>
            <option
              v-for="et in entityTypes"
              :key="et.type_name"
              :value="et.type_name"
            >
              {{ et.type_name }}
            </option>
          </select>
        </div>

        <!-- Entity Multi-Select -->
        <div v-if="selectedType" class="max-w-64">
          <label class="block text-xs font-medium text-gray-600 mb-1">{{ selectedType }}</label>
          <SimpleMultiSelect
            v-model="pickerIds"
            :options="entityOptions"
            :sensitive-labels="sensitiveLabels"
            :placeholder="'All ' + selectedType"
            :searchable="true"
            @change="onEntityChange"
          />
        </div>

        <!-- Patient Search Fields -->
        <template v-if="patientSearch">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
            <input v-model="searchLastName" type="text" placeholder="Smith"
              class="sensitive w-28 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400"
              @input="onSearchDebounced" @keydown.enter="emitChange" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">First Name</label>
            <input v-model="searchFirstName" type="text" placeholder="John"
              class="sensitive w-28 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400"
              @input="onSearchDebounced" @keydown.enter="emitChange" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">DOB</label>
            <input v-model="searchDob" type="text" placeholder="1952-03-15"
              class="sensitive w-28 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400"
              @input="onSearchDebounced" @keydown.enter="emitChange" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">MBI</label>
            <input v-model="searchMbi" type="text" placeholder="1A2B3C4D5E6"
              class="sensitive w-28 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400"
              @input="onSearchDebounced" @keydown.enter="emitChange" />
          </div>
        </template>

        <!-- My Practices Toggle -->
        <button
          v-if="showMyPractices"
          @click="toggleMyPractices"
          :class="[
            'px-2 py-1 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            myPracticesActive
              ? 'bg-primary-500 text-white border-primary-500'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          {{ myPracticesLabel }}
        </button>

        <!-- InfoTag Filters -->
        <InfoTagFilter
          v-for="tagType in infoTagTypes"
          :key="tagType.type"
          :model-value="selectedInfoTags[tagType.type] || []"
          :info-tag-type="tagType.type"
          :label="tagType.label || tagType.type"
          :button-label="tagType.buttonLabel || 'Tag'"
          :color="tagType.color || 'emerald'"
          @change="(ids) => onInfoTagChange(tagType.type, ids)"
        />

        <!-- Demographic Filters (optional) -->
        <DemographicFilterDropdowns
          v-if="demographicFilters.length > 0"
          ref="demographicRef"
          :enabled-filters="demographicFilters"
          @change="onDemographicChange"
        />

        <!-- Patient Flag Filter (optional) -->
        <div v-if="patientFlagsScope && flagOptions.length > 0" class="max-w-56">
          <label class="block text-xs font-medium text-gray-600 mb-1">Flags</label>
          <SimpleMultiSelect
            v-model="selectedFlagKeys"
            :options="flagOptions"
            :sensitive-labels="false"
            placeholder="All Flags"
            @change="emitChange"
          />
        </div>
      </template>

      <!-- Module-specific filters slot (always visible — parent has saved state) -->
      <slot name="filters" />

      <!-- Clear All -->
      <button
        v-if="hasActiveFilters && !hideClear"
        @click="clearAll"
        class="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap"
      >
        <FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />
        Clear
      </button>
    </div>

    <!-- Active Filter Tags -->
    <div v-if="filterTags.length > 0" class="mt-2 flex flex-wrap gap-2">
      <span
        v-for="tag in filterTags"
        :key="tag.key"
        class="inline-flex items-center px-2 py-1 rounded-md text-xs bg-primary-100 text-primary-700"
      >
        <span v-if="tag.entityName != null">{{ tag.typeName }}: <span :class="{ sensitive: sensitiveLabels }" :data-spoof-kind="entitySpoofKind(tag.typeName, tag.entityName)">{{ tag.entityName }}</span></span>
        <span v-else>{{ tag.label }}</span>
        <button
          @click="removeFilter(tag.key)"
          class="ml-1 hover:text-primary-900"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import InfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/InfoTagFilter.vue';
import DemographicFilterDropdowns from '@/components/widgetcomponents/Dropdown_Selector/DemographicFilterDropdowns.vue';
import { entitySpoofKind } from '@/utils/demoSpoof';

const DEFAULT_EXCLUDE = ['Patient', 'Vendor'];

export default {
  name: 'Worklist_FilterBar',
  components: { FontAwesomeIcon, SimpleMultiSelect, InfoTagFilter, DemographicFilterDropdowns },
  props: {
    sensitiveLabels: { type: Boolean, default: true },
    showMyPractices: { type: Boolean, default: true },
    myPracticesLabel: { type: String, default: 'My Practices' },
    storageKey: {
      type: String,
      default: null
    },
    hideClear: {
      type: Boolean,
      default: false
    },
    initialFilters: {
      type: Object,
      default: null
    },
    excludeTypes: {
      type: Array,
      default: () => DEFAULT_EXCLUDE
    },
    patientSearch: {
      type: Boolean,
      default: false
    },
    infoTagTypes: {
      type: Array,
      default: () => []
      // e.g., [{ type: 'Implementation Status', label: 'Status', color: 'emerald' }]
    },
    demographicFilters: {
      type: Array,
      default: () => []
      // e.g., ['race_ethnicity', 'language']
    },
    patientFlagsScope: {
      type: String,
      default: null
      // e.g., 'quality'
    }
  },
  emits: ['filter-changed'],
  data() {
    return {
      loading: true,
      entityTypes: [],
      selectedType: null,
      entities: [],       // entities loaded for the current type dropdown
      pickerIds: [],       // IDs currently checked in the multi-select
      activeFilters: [],   // accumulated: [{ id, name, typeName }]
      myPracticesActive: false,
      permittedEntityIds: null, // null = unrestricted, Set = restricted to these IDs
      searchLastName: '',
      searchFirstName: '',
      searchDob: '',
      searchMbi: '',
      searchDebounceTimer: null,
      selectedInfoTags: {},  // { tagTypeName: [tagId1, tagId2, ...] }
      demographicSelections: null,  // { race_ethnicity: [], languages: [] } — set by child component
      flagOptions: [],
      selectedFlagKeys: []
    };
  },
  computed: {
    hasActiveFilters() {
      return this.activeFilters.length > 0 || this.myPracticesActive ||
        this.searchLastName || this.searchFirstName || this.searchDob || this.searchMbi ||
        this.allInfoTagIds.length > 0 || this.hasDemographicSelections ||
        this.selectedFlagKeys.length > 0;
    },
    hasDemographicSelections() {
      if (!this.demographicSelections) return false;
      return (this.demographicSelections.race_ethnicity?.length > 0) ||
             (this.demographicSelections.languages?.length > 0);
    },
    allInfoTagIds() {
      return Object.values(this.selectedInfoTags).flat();
    },
    entityOptions() {
      return this.entities
        .map(e => ({ value: e.id, label: e.name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    entityFilters() {
      if (this.activeFilters.length === 0) return null;
      const grouped = {};
      for (const f of this.activeFilters) {
        if (!grouped[f.typeName]) grouped[f.typeName] = [];
        grouped[f.typeName].push(f.id);
      }
      return grouped;
    },
    // Parallel to entityFilters but carrying names — consumers that want a
    // human-readable scope label (e.g. PPTX export header) use this.
    entityNames() {
      if (this.activeFilters.length === 0) return null;
      const grouped = {};
      for (const f of this.activeFilters) {
        if (!grouped[f.typeName]) grouped[f.typeName] = [];
        grouped[f.typeName].push(f.name);
      }
      return grouped;
    },
    filterTags() {
      const tags = [];
      // No pill for My Practices / My Contracts: the highlighted button shows it.
      for (const f of this.activeFilters) {
        tags.push({ key: `entity-${f.id}`, label: `${f.typeName}: ${f.name}`, typeName: f.typeName, entityName: f.name });
      }
      if (this.$refs.demographicRef) {
        tags.push(...this.$refs.demographicRef.getFilterTags());
      }
      return tags;
    }
  },
  methods: {
    entitySpoofKind,
    async loadEntityTypes() {
      try {
        const response = await this.$axios.get('/api/entity-types/', {
          params: { include_counts: true }
        });
        const types = response.data.results || response.data;
        this.entityTypes = types
          .filter(t => t.is_active !== false && !this.excludeTypes.includes(t.type_name))
          .filter(t => t.entity_count === null || t.entity_count === undefined || t.entity_count > 0)
          .sort((a, b) => a.type_name.localeCompare(b.type_name));
      } catch (error) {
        console.error('Error loading entity types:', error);
      }
    },

    async loadPatientFlags() {
      if (!this.patientFlagsScope) return;
      try {
        const { data } = await this.$axios.get('/api/impact_analytics/composable-metrics/patient-flags/', {
          params: { enabled_for: this.patientFlagsScope }
        });
        this.flagOptions = (data?.flags || []).map(f => ({ value: f.key, label: f.label }));
      } catch (error) {
        console.error('Error loading patient flags:', error);
        this.flagOptions = [];
      }
    },

    async loadUserScope() {
      try {
        const response = await this.$axios.get('/api/worklist/user-scope/');
        if (response.data.is_restricted && response.data.entity_ids) {
          this.permittedEntityIds = new Set(response.data.entity_ids);
        }
      } catch (error) {
        console.error('Error loading user scope:', error);
      }
    },

    async loadEntitiesForType(typeName) {
      try {
        const params = { entity_type_name: typeName };
        // Scope to descendants of already-selected entities, but only
        // within the same hierarchy. Practice and Contract/Payer are
        // separate trees — cross-hierarchy ancestor_ids returns nothing.
        const CONTRACT_TYPES = new Set(['Contract', 'Contract Performance Year', 'Contract Pool', 'Payer']);
        const targetIsContract = CONTRACT_TYPES.has(typeName);
        const otherFilterIds = this.activeFilters
          .filter(f => f.typeName !== typeName && CONTRACT_TYPES.has(f.typeName) === targetIsContract)
          .map(f => f.id);
        if (otherFilterIds.length > 0) {
          params.ancestor_ids = otherFilterIds.join(',');
        }
        const response = await this.$axios.get('/api/entities/', { params });
        let results = response.data.results || response.data;
        if (this.permittedEntityIds) {
          results = results.filter(e => this.permittedEntityIds.has(e.id));
        }
        this.entities = results.map(e => ({
          id: e.id,
          name: e.entity_name || e.name
        }));
      } catch (error) {
        console.error('Error loading entities:', error);
        this.entities = [];
      }
    },

    async onTypeChange() {
      this.entities = [];
      this.pickerIds = [];
      if (this.selectedType) {
        await this.loadEntitiesForType(this.selectedType);
        // Restore any existing selections for this type in the picker
        const existingIds = this.activeFilters
          .filter(f => f.typeName === this.selectedType)
          .map(f => f.id);
        this.pickerIds = existingIds;
      }
    },

    onEntityChange() {
      // Replace activeFilters for the current type with new picker selections
      this.activeFilters = this.activeFilters.filter(f => f.typeName !== this.selectedType);
      for (const id of this.pickerIds) {
        const entity = this.entities.find(e => e.id === id);
        if (entity) {
          this.activeFilters.push({ id, name: entity.name, typeName: this.selectedType });
        }
      }
      this.saveToStorage();
      this.emitChange();
    },

    toggleMyPractices() {
      this.myPracticesActive = !this.myPracticesActive;
      this.emitChange();
    },

    removeFilter(key) {
      if (key.startsWith('entity-')) {
        const id = parseInt(key.replace('entity-', ''));
        this.activeFilters = this.activeFilters.filter(f => f.id !== id);
        // Also uncheck in picker if viewing that type
        this.pickerIds = this.pickerIds.filter(v => v !== id);
      } else if (key.startsWith('demo-') && this.$refs.demographicRef) {
        this.$refs.demographicRef.removeFilter(key);
        this.demographicSelections = {
          race_ethnicity: this.$refs.demographicRef.selectedRaceEthnicity,
          languages: this.$refs.demographicRef.selectedLanguages
        };
      }
      this.saveToStorage();
      this.emitChange();
    },

    clearAll() {
      this.activeFilters = [];
      this.pickerIds = [];
      this.myPracticesActive = false;
      this.selectedInfoTags = {};
      this.selectedFlagKeys = [];
      this.searchLastName = '';
      this.searchFirstName = '';
      this.searchDob = '';
      this.searchMbi = '';
      if (this.$refs.demographicRef) {
        this.$refs.demographicRef.clearAll();
        this.demographicSelections = null;
      }
      this.saveToStorage();
      this.emitChange();
    },

    onSearchDebounced() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.emitChange();
      }, 300);
    },

    emitChange() {
      const payload = {
        entity_filters: this.entityFilters,
        entity_names: this.entityNames,
        my_practices: this.myPracticesActive || undefined,
        infotag_filters: this.allInfoTagIds.length > 0 ? this.allInfoTagIds : undefined,
        flag_keys: this.selectedFlagKeys.length > 0 ? [...this.selectedFlagKeys] : undefined,
      };
      if (this.patientSearch) {
        payload.search_last_name = this.searchLastName || undefined;
        payload.search_first_name = this.searchFirstName || undefined;
        payload.search_dob = this.searchDob || undefined;
        payload.search_mbi = this.searchMbi || undefined;
      }
      if (this.hasDemographicSelections) {
        payload.demographic_filters = this.demographicSelections;
      }
      this.$emit('filter-changed', payload);
    },

    onInfoTagChange(typeName, tagIds) {
      this.selectedInfoTags = { ...this.selectedInfoTags, [typeName]: tagIds };
      this.emitChange();
    },

    onDemographicChange(selections) {
      this.demographicSelections = selections;
      this.emitChange();
    },

    saveToStorage() {
      if (!this.storageKey) return;
      if (this.activeFilters.length > 0) {
        localStorage.setItem(this.storageKey, JSON.stringify({
          filters: this.activeFilters,
          type: this.selectedType
        }));
      } else {
        localStorage.removeItem(this.storageKey);
      }
    },

    async restoreFilters() {
      let state = null;

      if (this.storageKey) {
        const saved = localStorage.getItem(this.storageKey);
        if (!saved) return;
        try {
          state = JSON.parse(saved);
        } catch {
          localStorage.removeItem(this.storageKey);
          return;
        }
      } else if (this.initialFilters) {
        state = this.initialFilters;
      }

      if (!state) return;

      if (state.myPracticesActive) {
        this.myPracticesActive = true;
      }
      if (Array.isArray(state.flagKeys) && state.flagKeys.length) {
        this.selectedFlagKeys = [...state.flagKeys];
      }

      if (!state?.filters?.length && !this.myPracticesActive && !this.selectedFlagKeys.length) return;

      this.activeFilters = state.filters || [];
      if (state.type) {
        this.selectedType = state.type;
        await this.loadEntitiesForType(state.type);
        this.pickerIds = this.activeFilters
          .filter(f => f.typeName === this.selectedType)
          .map(f => f.id);
      }
      this.emitChange();
    }
  },
  async mounted() {
    await this.loadUserScope();
    await Promise.all([this.loadEntityTypes(), this.loadPatientFlags()]);
    await this.restoreFilters();
    this.loading = false;
  }
};
</script>
